import React from 'react';
import { Link } from 'react-router-dom';
import { formatVolume, pipelineRecords, sumVolume } from '../data/pipeline';
import { monthlyProduction, production } from '../data/production';
const compact = (value: number): string => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1
}).format(value);
export function ProductionSnapshot() {
  const active = pipelineRecords.filter(record => record.bucket === 'active');
  const potential = pipelineRecords.filter(record => record.bucket === 'potential');
  const goalPercent = Math.min(100, Math.round(production.mtdVolume / production.goalVolume * 100));
  const maxUnits = Math.max(...monthlyProduction.map(month => month.units));
  return <section className="rounded-xl border border-gray-200 bg-white" aria-labelledby="production-heading" data-id="element-569" data-mp="1c9od9wdfqo8j">
      <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3.5" data-id="element-570" data-mp="1h89f208c5fz3">
        <h2 id="production-heading" className="text-[14px] font-semibold text-ink" data-id="element-571" data-mp="3f0tsbc66vl7y">
          My production · August
        </h2>
        <Link to="/reports" className="text-[12.5px] font-medium text-brand hover:text-brand-dark" data-id="element-572" data-mp="3a2ijn4rmrgrb">
          Reports
        </Link>
      </div>

      <div className="space-y-4 px-5 py-4" data-id="element-573" data-mp="3j4jbfm43hall">
        <div data-id="element-574" data-mp="3j1nh07rgh41w">
          <div className="flex items-baseline gap-2" data-id="element-575" data-mp="2vdmd5c582bst">
            <p className="text-[24px] font-bold leading-none text-ink" data-id="element-576" data-mp="2vef87futjcv4">
              {formatVolume(production.mtdVolume)}
            </p>
            <p className="text-[12.5px] text-gray-500" data-id="element-577" data-mp="353p3f4kylbng">
              of {compact(production.goalVolume)} goal
            </p>
            <p className="ml-auto text-[12.5px] font-semibold text-brand" data-id="element-578" data-mp="30cqitssxtree">{goalPercent}%</p>
          </div>
          <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-gray-100" role="progressbar" aria-valuenow={goalPercent} aria-valuemin={0} aria-valuemax={100} aria-label="Monthly volume goal progress" data-id="element-579" data-mp="2cmcqe16fxqja">
            <div className="h-full rounded-full bg-brand" style={{
            width: `${goalPercent}%`
          }} data-id="element-580" data-mp="gjhq4cg43ngj" />
          </div>
          <p className="mt-1.5 text-[12px] text-gray-500" data-id="element-581" data-mp="ld2po8xcsphu">
            {production.mtdUnits} of {production.goalUnits} units funded ·{' '}
            {production.goalUnits - production.mtdUnits} to hit goal
          </p>
        </div>

        <dl className="grid grid-cols-2 gap-3" data-id="element-582" data-mp="3tq9rm46dml76">
          <div className="rounded-lg bg-gray-50 px-3 py-2.5" data-id="element-583" data-mp="3tr1m1bo8mcvv">
            <dt className="text-[10.5px] uppercase tracking-wide text-gray-500" data-id="element-584" data-mp="2k8k25lme9qa">
              Active pipeline
            </dt>
            <dd className="mt-0.5 text-[14px] font-semibold text-ink" data-id="element-585" data-mp="6nn18zlaow5b">
              {active.length} · {compact(sumVolume(active))}
            </dd>
          </div>
          <div className="rounded-lg bg-gray-50 px-3 py-2.5" data-id="element-586" data-mp="1i0h8gcqhcyj1">
            <dt className="text-[10.5px] uppercase tracking-wide text-gray-500" data-id="element-587" data-mp="1i33f8a5jra07">
              Potential pipeline
            </dt>
            <dd className="mt-0.5 text-[14px] font-semibold text-ink" data-id="element-588" data-mp="2yh68i28ule23">
              {potential.length} · {compact(sumVolume(potential))}
            </dd>
          </div>
          <div className="rounded-lg bg-gray-50 px-3 py-2.5" data-id="element-589" data-mp="2orwcwcespd98">
            <dt className="text-[10.5px] uppercase tracking-wide text-gray-500" data-id="element-590" data-mp="2ord4pney638e">
              Pull-through
            </dt>
            <dd className="mt-0.5 text-[14px] font-semibold text-ink" data-id="element-591" data-mp="2jqfd6k81rwu8">
              {Math.round(production.pullThrough * 100)}%
            </dd>
          </div>
          <div className="rounded-lg bg-gray-50 px-3 py-2.5" data-id="element-592" data-mp="2aswnh2jgkuc0">
            <dt className="text-[10.5px] uppercase tracking-wide text-gray-500" data-id="element-593" data-mp="25tabsxja3m5b">
              Avg days to close
            </dt>
            <dd className="mt-0.5 text-[14px] font-semibold text-ink" data-id="element-594" data-mp="8zpba1z56suc">
              {production.avgDaysToClose}
            </dd>
          </div>
        </dl>

        <div data-id="element-595" data-mp="2ww4eub6qosbc">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-596" data-mp="2wvmcqxxebjvi">
            Funded units · last 6 months
          </p>
          <div className="flex items-end gap-2" data-id="element-597" data-mp="31ufpgo4uz6um">
            {monthlyProduction.map((month, index) => {
            const isCurrent = index === monthlyProduction.length - 1;
            return <div key={month.month} className="flex flex-1 flex-col items-center gap-1.5" data-id="element-598" data-mp="2sspr0yck77nc" data-mpi={index}>
                  <span className="text-[11px] font-medium text-gray-600" data-id="element-599" data-mp="2e4bpcqqvpp8d">
                    {month.units}
                  </span>
                  <span className="flex h-[64px] w-full items-end" data-id="element-600" data-mp="254y9ngc5w4f7">
                    <span className={['w-full rounded-t', isCurrent ? 'bg-brand' : 'bg-navy/25'].join(' ')} style={{
                  height: `${month.units / maxUnits * 100}%`
                }} title={`${month.month}: ${month.units} units · ${compact(month.volume)}`} data-id="element-601" data-mp="256svcwv9c0kc" />
                  </span>
                  <span className="text-[10.5px] text-gray-500" data-id="element-602" data-mp="1bl091t2t193w">{month.month}</span>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}